import React from "react";
import { PlanCardButton } from "./PlanCardButton";

const tierStyles = {
    good: { container: "border-slate-200 bg-white", badge: null, button: "bg-slate-900 hover:bg-slate-800 text-white" },
    better: { container: "border-2 border-indigo-500 bg-white shadow-xl ring-1 ring-indigo-500/20", badge: "bg-indigo-600 text-white", button: "bg-indigo-600 hover:bg-indigo-700 text-white" },
    best: { container: "border-slate-200 bg-white", badge: null, button: "bg-slate-900 hover:bg-slate-800 text-white" },
};

export function PlanCard({
    plan, tier, isCurrentPlan, isLoading, needsResume, isPaused, isCanceling,
    isUpgrade, isDowngrade, isBillingCycleChange, hasActiveSubscription,
    onSelect, onResume, isYearly, t,
}) {
    const styles = tierStyles[tier] || tierStyles.good;
    const displayPrice = isYearly ? plan.priceYearly : plan.price;
    const allFeatures = plan.keyFeatures || plan.features.slice(0, 4);
    const keyFeatures = allFeatures.filter(f => !/(cr[eé]dit|credit)/i.test(f));

    return (
        <div className={`relative rounded-2xl p-6 md:p-8 flex flex-col h-full transition-all duration-300 hover:shadow-lg ${styles.container} ${tier === "better" ? "md:scale-105 md:-my-4 z-10" : ""}`}>
            {tier === "better" && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className={`${styles.badge} px-4 py-1.5 rounded-full text-sm font-semibold tracking-wide`}>
                        {t("billing.mostPopular") || "MOST POPULAR"}
                    </span>
                </div>
            )}

            {isCurrentPlan && (
                <div className="absolute top-4 right-4 flex flex-col gap-1 items-end">
                    <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-semibold">{t("billing.current")}</span>
                    {isPaused && <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium border border-blue-200">{t("billing.paused") || "Paused"}</span>}
                    {isCanceling && <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs font-medium border border-amber-200">{t("billing.cancelingAtPeriodEnd") || "Canceling"}</span>}
                </div>
            )}

            <div className="mb-6">
                <h3 className="text-xl font-bold text-slate-900 mb-2">{plan.nameKey ? t(`billing.planNames.${plan.nameKey}`, { defaultValue: plan.name }) : plan.name}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{plan.descriptionKey ? t(plan.descriptionKey, { defaultValue: plan.description }) : plan.description}</p>
            </div>

            <div className="mb-6">
                <div className="flex items-baseline gap-1">
                    <span className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">${displayPrice}</span>
                    <span className="text-slate-500 text-sm">/month</span>
                </div>
                {isYearly && <p className="text-emerald-600 text-sm mt-1 font-medium">{t("billing.billedAnnually") || "Billed annually"}</p>}
            </div>

            {isBillingCycleChange && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-4">
                    <div className="text-sm font-medium text-emerald-800">{isYearly ? t("billing.switchToYearly") || "Switch to yearly billing" : t("billing.switchToMonthly") || "Switch to monthly billing"}</div>
                    {isYearly && plan.savings > 0 && <div className="text-xs text-emerald-600 mt-1">{t("billing.saveDollarsPerYear", { amount: plan.savings }) || `Save $${plan.savings}/year`}</div>}
                </div>
            )}

            <ul className="space-y-3 mb-8 flex-grow">
                {keyFeatures.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                        <svg className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-slate-700 text-sm">{feature}</span>
                    </li>
                ))}
                {plan.credits && (
                    <li className="flex items-start gap-3">
                        <svg className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-slate-400 text-xs">{t("billing.aiCapacityPerMonth") || "AI capacity per month"} {plan.credits} · ≈ {Math.round(parseInt(plan.credits) / 20)}–{Math.round(parseInt(plan.credits) / 10)} {t("billing.appScreens") || "app screens"}</span>
                    </li>
                )}
            </ul>

            <PlanCardButton
                plan={plan} tier={tier} isCurrentPlan={isCurrentPlan} isLoading={isLoading}
                needsResume={needsResume} isUpgrade={isUpgrade} isDowngrade={isDowngrade}
                isBillingCycleChange={isBillingCycleChange} hasActiveSubscription={hasActiveSubscription}
                isYearly={isYearly} buttonStyle={styles.button} onSelect={onSelect} onResume={onResume} t={t}
            />

            {!isCurrentPlan && <p className="text-xs text-slate-400 text-center mt-3">{t("billing.microReassurance")}</p>}
        </div>
    );
}
